/* kerandy-booking.js — Appointment booking modal with live availability */
(function () {
'use strict';

const CFG = {
  businessName: 'Kerandy Hair Braiding',
  address: '2414 W 10th St, Greeley, CO 80634',
  phone: '970-397-4543',
  openMinutes: 8 * 60,        // 8:00 AM — first bookable start time
  lastStartMinutes: 17 * 60,  // 5:00 PM — last bookable start time
  availableDays: [0,1,2,3,4,5,6], // Every day
  // Map lowercased service keywords → duration in minutes. Add entries here.
  serviceDurations: {},
  defaultDurationMinutes: 120,
  maxWeeksAhead: 8
};

// Steps: 1=Service 2=Date 3=Time 4=Details 5=Review 6=Success
const STEP_IDS = ['kd-step-service','kd-step-date','kd-step-time','kd-step-contact','kd-step-review','kd-step-success'];

const state = {
  step: 1,
  calYear: 0, calMonth: 0,
  selectedDate: null, selectedSlot: null,
  booking: {}
};

/*
 * avail cache: { "YYYY-MM-DD": {status:'loading'|'loaded', busy:[{start,end}], allDayBlocked:bool} }
 * monthCache:  { "YYYY-MM":   {status:'loading'|'loaded', blocked: Set<"YYYY-MM-DD">} }
 *
 * allDayBlocked=true → a Google Calendar all-day event blocks that date entirely.
 * Holidays do NOT block availability — only explicit calendar events do.
 */
const avail      = {};
const monthCache = {};

const $ = id => document.getElementById(id);
const pad = n => String(n).padStart(2,'0');
const MONTHS_L = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_S = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS_L   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/* ── Helpers ───────────────────────────────────────────────────────────── */
function today()        { const d = new Date(); d.setHours(0,0,0,0); return d; }
function maxDate()      { const d = today(); d.setDate(d.getDate() + CFG.maxWeeksAhead * 7); return d; }
function dateKey(d)     { return d.getFullYear() + '-' + pad(d.getMonth()+1) + '-' + pad(d.getDate()); }
function monthKey(y, m) { return y + '-' + pad(m + 1); }

/* Match service text against known keywords to determine duration */
function getServiceDuration() {
  const svc = (state.booking.service || '').toLowerCase();
  for (const [key, dur] of Object.entries(CFG.serviceDurations)) {
    if (svc.includes(key)) return dur;
  }
  return CFG.defaultDurationMinutes;
}

/* "8am", "10:30am", "2pm" */
function formatMins(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return (h % 12 || 12) + (m ? ':' + pad(m) : '') + (h >= 12 ? 'pm' : 'am');
}

/* "8:00 AM", "2:30 PM" */
function formatMinsLong(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return (h % 12 || 12) + ':' + pad(m) + ' ' + (h >= 12 ? 'PM' : 'AM');
}

/*
 * Returns [[startMin, endMin], …] of valid appointment START times for the day.
 * A start T is valid when [T, T+duration] doesn't overlap any busy period.
 */
function getAvailableWindows(busyList) {
  const D    = getServiceDuration();
  const OPEN = CFG.openMinutes;
  const LAST = CFG.lastStartMinutes;
  const busy = [...busyList].sort((a, b) => a.start - b.start);
  const wins = [];
  let cursor = OPEN;
  for (const b of busy) {
    const winEnd = Math.min(LAST, b.start - D);
    if (winEnd >= cursor) wins.push([cursor, winEnd]);
    cursor = Math.max(cursor, b.end);
  }
  if (cursor <= LAST) wins.push([cursor, LAST]);
  return wins;
}

function formatWindows(wins) {
  if (!wins.length) return 'none';
  return wins
    .map(([s, e]) => s === e ? formatMins(s) : formatMins(s) + '–' + formatMins(e))
    .join(' or ');
}

/* Parse typed time → minutes from midnight, or null */
function parseTime(str) {
  str = str.trim().toLowerCase().replace(/\s+/g, '');
  let m = str.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/);
  if (m) {
    let h = parseInt(m[1]);
    const mn = m[2] ? parseInt(m[2]) : 0;
    if (m[3] === 'pm' && h !== 12) h += 12;
    if (m[3] === 'am' && h === 12) h = 0;
    if (h < 0 || h > 23 || mn < 0 || mn > 59) return null;
    return h * 60 + mn;
  }
  m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = parseInt(m[1]), mn = parseInt(m[2]);
    if (h < 0 || h > 23 || mn < 0 || mn > 59) return null;
    return h * 60 + mn;
  }
  return null;
}

function isTimeInWindows(minutes, wins) {
  return wins.some(([s, e]) => minutes >= s && minutes <= e);
}

function isDateBlockedByAllDay(date) {
  const mc = monthCache[monthKey(date.getFullYear(), date.getMonth())];
  return mc && mc.status === 'loaded' && mc.blocked.has(dateKey(date));
}

/* ── Month all-day fetch (JSONP) ─────────────────────────────────────── */
function fetchMonthAllDay(year, month) {
  const mk = monthKey(year, month);
  if (monthCache[mk]) return;
  const url = window.KERANDY_APPS_SCRIPT_URL;
  if (!url) { monthCache[mk] = {status:'loaded', blocked: new Set()}; return; }

  monthCache[mk] = {status:'loading', blocked: new Set()};
  const cbName = 'kdMo_' + mk.replace(/-/g,'');

  window[cbName] = function(data) {
    delete window[cbName];
    const blocked = new Set(data.blocked || []);
    monthCache[mk] = {status:'loaded', blocked};
    if (state.calYear === year && state.calMonth === month) {
      blocked.forEach(dk => {
        const cell = document.querySelector('.kd-bk-cal-day[data-date="' + dk + '"]');
        if (cell && !cell.disabled) { cell.disabled = true; cell.classList.add('kd-bk-cal-day--off'); }
      });
    }
  };

  const script = document.createElement('script');
  script.src = url + '?action=allday&year=' + year + '&month=' + (month + 1) + '&callback=' + cbName;
  script.onerror = () => { delete window[cbName]; monthCache[mk] = {status:'loaded', blocked: new Set()}; };
  document.head.appendChild(script);

  setTimeout(() => {
    if (monthCache[mk] && monthCache[mk].status === 'loading')
      monthCache[mk] = {status:'loaded', blocked: new Set()};
  }, 6000);
}

/* ── Per-day availability fetch (JSONP) ──────────────────────────────── */
function fetchAvailability(date) {
  const key = dateKey(date);
  if (avail[key]) return;
  const url = window.KERANDY_APPS_SCRIPT_URL;
  if (!url) { avail[key] = {status:'loaded', busy:[], allDayBlocked:false}; return; }

  avail[key] = {status:'loading', busy:[], allDayBlocked:false};
  const cbName = 'kdAv_' + key.replace(/-/g,'');

  window[cbName] = function(data) {
    delete window[cbName];
    avail[key] = {status:'loaded', busy: data.busy || [], allDayBlocked: !!data.allDayBlocked};
    if (state.selectedDate && dateKey(state.selectedDate) === key && state.step === 3) renderSlots();
  };

  const script = document.createElement('script');
  script.src = url + '?action=availability&date=' + key + '&callback=' + cbName;
  script.onerror = () => {
    avail[key] = {status:'loaded', busy:[], allDayBlocked:false};
    if (state.step === 3 && state.selectedDate && dateKey(state.selectedDate) === key) renderSlots();
  };
  document.head.appendChild(script);

  setTimeout(() => {
    if (avail[key] && avail[key].status === 'loading') {
      avail[key] = {status:'loaded', busy:[], allDayBlocked:false};
      if (state.step === 3 && state.selectedDate && dateKey(state.selectedDate) === key) renderSlots();
    }
  }, 6000);
}

/* ── Calendar rendering ──────────────────────────────────────────────── */
function renderCal(year, month) {
  state.calYear = year; state.calMonth = month;
  const label = $('kd-cal-month-label');
  if (label) label.textContent = MONTHS_L[month] + ' ' + year;
  const grid = $('kd-bk-cal-grid');
  if (!grid) return;
  grid.querySelectorAll('.kd-bk-cal-day').forEach(c => c.remove());

  fetchMonthAllDay(year, month);

  const first = new Date(year, month, 1).getDay();
  const days  = new Date(year, month + 1, 0).getDate();
  const tdy = today(), mx = maxDate();

  for (let i = 0; i < first; i++) {
    const b = document.createElement('div');
    b.className = 'kd-bk-cal-day kd-bk-cal-day--blank';
    grid.appendChild(b);
  }

  for (let d = 1; d <= days; d++) {
    const date = new Date(year, month, d);
    const dk   = dateKey(date);
    const inRange     = CFG.availableDays.includes(date.getDay()) && date >= tdy && date <= mx;
    const allDayBlock = isDateBlockedByAllDay(date);
    const isAvail     = inRange && !allDayBlock;
    const isSel       = state.selectedDate && date.toDateString() === state.selectedDate.toDateString();
    const isToday     = date.toDateString() === tdy.toDateString();

    const cell = document.createElement('button');
    cell.type = 'button';
    cell.dataset.date = dk;
    cell.className = [
      'kd-bk-cal-day',
      !isAvail && 'kd-bk-cal-day--off',
      isSel    && 'kd-bk-cal-day--sel',
      isToday  && 'kd-bk-cal-day--today'
    ].filter(Boolean).join(' ');
    cell.textContent = d;
    cell.disabled    = !isAvail;

    if (isAvail) {
      cell.addEventListener('click', () => onSelectDate(date));
      cell.addEventListener('mouseenter', () => fetchAvailability(date), {once:true});
    }
    grid.appendChild(cell);
  }
}

function onSelectDate(date) {
  if (isDateBlockedByAllDay(date)) return;
  state.selectedDate = date;
  renderCal(state.calYear, state.calMonth);
  fetchAvailability(date);
  goStep(3);
}

/* ── Time step ───────────────────────────────────────────────────────── */
let _validationTimer = null;

function renderSlots() {
  const d = state.selectedDate; if (!d) return;
  const disp = $('kd-bk-date-display');
  if (disp) disp.textContent = DAYS_L[d.getDay()] + ', ' + MONTHS_S[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();

  const availText  = $('kd-bk-avail-text');
  const timeInput  = $('kd-bk-time-input');
  const timeStatus = $('kd-bk-time-status');
  const nxtBtn     = $('kd-bk-next');

  state.selectedSlot = null;
  if (timeInput)  { timeInput.value = ''; timeInput.disabled = false; }
  if (timeStatus) { timeStatus.hidden = true; timeStatus.textContent = ''; timeStatus.className = 'kd-bk-time-status'; }
  if (nxtBtn)     nxtBtn.disabled = true;

  const key = dateKey(d), cache = avail[key];

  if (!cache || cache.status === 'loading') {
    if (availText) availText.textContent = 'Checking availability…';
    if (timeInput) timeInput.disabled = true;
    return;
  }

  if (cache.allDayBlocked) {
    if (availText) availText.textContent = 'This day is fully booked — please choose another date.';
    if (timeInput) timeInput.disabled = true;
    return;
  }

  const windows = getAvailableWindows(cache.busy);
  if (!windows.length) {
    if (availText) availText.textContent = 'No available times — please choose another date.';
    if (timeInput) timeInput.disabled = true;
    return;
  }

  if (availText) availText.textContent = 'Available times: ' + formatWindows(windows);
}

function validateTimeInput() {
  const timeInput  = $('kd-bk-time-input');
  const timeStatus = $('kd-bk-time-status');
  const nxtBtn     = $('kd-bk-next');
  if (!timeInput || !timeStatus) return;

  const raw = timeInput.value.trim();
  if (!raw) {
    timeStatus.hidden = true;
    state.selectedSlot = null;
    if (nxtBtn) nxtBtn.disabled = true;
    return;
  }

  const minutes = parseTime(raw);
  const d     = state.selectedDate;
  const key   = d && dateKey(d);
  const cache = key ? avail[key] : null;
  const wins  = (cache && cache.status === 'loaded' && !cache.allDayBlocked)
    ? getAvailableWindows(cache.busy) : [];

  timeStatus.hidden = false;

  if (minutes === null) {
    timeStatus.textContent = 'Please enter a valid time (e.g. 10:30am or 2pm).';
    timeStatus.className = 'kd-bk-time-status kd-bk-time-status--error';
    state.selectedSlot = null;
    if (nxtBtn) nxtBtn.disabled = true;
    return;
  }

  if (isTimeInWindows(minutes, wins)) {
    const h = Math.floor(minutes / 60), m = minutes % 60;
    state.selectedSlot = {h, m, label: formatMinsLong(minutes)};
    timeStatus.textContent = 'Time is available';
    timeStatus.className = 'kd-bk-time-status kd-bk-time-status--ok';
    if (nxtBtn) nxtBtn.disabled = false;
  } else {
    timeStatus.textContent = 'Time is occupied, please select another.';
    timeStatus.className = 'kd-bk-time-status kd-bk-time-status--error';
    state.selectedSlot = null;
    if (nxtBtn) nxtBtn.disabled = true;
  }
}

/* ── Contact validation ──────────────────────────────────────────────── */
function validateContact() {
  const n = $('kd-bk-name'), e = $('kd-bk-email'), p = $('kd-bk-phone');
  let ok = true;
  [n,e,p].forEach(el => el && el.classList.remove('kd-bk-field--error'));
  if (!n || !n.value.trim())                               { n && n.classList.add('kd-bk-field--error'); ok = false; }
  if (!e || !/^[^@]+@[^@]+\.[^@]+$/.test(e.value.trim())) { e && e.classList.add('kd-bk-field--error'); ok = false; }
  if (!p || !p.value.trim())                               { p && p.classList.add('kd-bk-field--error'); ok = false; }
  return ok;
}

/* ── Review ──────────────────────────────────────────────────────────── */
function renderReview() {
  const d = state.selectedDate, slot = state.selectedSlot; if (!d || !slot) return;
  const name  = ($('kd-bk-name') ||{}).value||'', email = ($('kd-bk-email')||{}).value||'';
  const phone = ($('kd-bk-phone')||{}).value||'', notes = ($('kd-bk-notes')||{}).value||'';
  Object.assign(state.booking, {name, email, phone, notes});

  const durMins  = getServiceDuration();
  const endMins  = slot.h * 60 + slot.m + durMins;
  const endLbl   = formatMinsLong(endMins);
  const durLabel = durMins >= 60
    ? (durMins / 60) + ' hr' + (durMins !== 60 ? 's' : '')
    : durMins + ' min';

  const box = $('kd-bk-review'); if (!box) return;
  box.innerHTML = [
    rv('Service', state.booking.service || '—'),
    rv('Date',    DAYS_L[d.getDay()] + ', ' + MONTHS_S[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear()),
    rv('Time',    slot.label + ' – ' + endLbl + ' (~' + durLabel + ')'),
    rv('Name',  name), rv('Email', email), rv('Phone', phone),
    notes && rv('Notes', notes)
  ].filter(Boolean).join('');
}
function rv(l,v) { return `<div class="kd-bk-review__row"><span>${l}</span><strong>${v}</strong></div>`; }

/* ── Submit ──────────────────────────────────────────────────────────── */
function submitBooking() {
  const btn = $('kd-bk-confirm');
  if (btn) { btn.disabled = true; btn.textContent = 'Confirming…'; }
  const d = state.selectedDate, slot = state.selectedSlot;
  const durMins = getServiceDuration();
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), slot.h, slot.m);
  const end   = new Date(start.getTime() + durMins * 60000);

  const payload = {
    name: state.booking.name, email: state.booking.email, phone: state.booking.phone,
    notes: state.booking.notes, service: state.booking.service || 'Braiding Service',
    datetime: start.toISOString(), durationMinutes: durMins,
    address: CFG.address, businessPhone: CFG.phone
  };

  const scriptUrl = window.KERANDY_APPS_SCRIPT_URL;
  if (scriptUrl) {
    try { fetch(scriptUrl, {method:'POST', mode:'no-cors', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload)}); }
    catch(_){}
  }

  /* Mark slot busy locally to prevent double-booking this session */
  const key = dateKey(d);
  if (!avail[key]) avail[key] = {status:'loaded', busy:[], allDayBlocked:false};
  avail[key].busy.push({start: slot.h*60+slot.m, end: slot.h*60+slot.m+durMins});

  const title = payload.service + ' at ' + CFG.businessName;
  const desc  = [
    'Service: ' + payload.service, '',
    'Customer: ' + payload.name, 'Phone: ' + payload.phone, 'Email: ' + payload.email,
    payload.notes && 'Notes: ' + payload.notes, '',
    CFG.businessName, CFG.address, CFG.phone
  ].filter(Boolean).join('\n');

  const gcal = $('kd-bk-gcal-link'); if (gcal) gcal.href = makeGCalUrl(title, desc, start, end, CFG.address);
  const icsBtn = $('kd-bk-ics-btn'); if (icsBtn) icsBtn.onclick = () => downloadICS(makeICS(title, desc, start, end, CFG.address), 'kerandy-appointment.ics');
  const sm = $('kd-bk-success-msg');
  if (sm) sm.textContent = payload.service + ' · ' + MONTHS_S[d.getMonth()] + ' ' + d.getDate() + ' at ' + slot.label + '. Confirmation sent to ' + payload.email + '.';

  goStep(6);
  if (btn) { btn.disabled = false; btn.textContent = 'Confirm Booking'; }
}

/* ── Calendar export ─────────────────────────────────────────────────── */
function fmtLocal(dt) { return dt.getFullYear()+pad(dt.getMonth()+1)+pad(dt.getDate())+'T'+pad(dt.getHours())+pad(dt.getMinutes())+'00'; }
function makeGCalUrl(title, desc, s, e, addr) {
  return 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
    '&text=' + encodeURIComponent(title) + '&dates=' + fmtLocal(s) + '/' + fmtLocal(e) +
    '&details=' + encodeURIComponent(desc) + '&location=' + encodeURIComponent(addr || CFG.address);
}
function makeICS(title, desc, s, e, addr) {
  return ['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//Kerandy//Booking//EN','METHOD:PUBLISH',
    'BEGIN:VEVENT','UID:'+s.getTime()+'@kerandy','DTSTAMP:'+fmtLocal(new Date()),
    'DTSTART;TZID=America/Denver:'+fmtLocal(s),'DTEND;TZID=America/Denver:'+fmtLocal(e),
    'SUMMARY:'+title.replace(/[,\n]/g,x=>x===','?'\\,':'\\n'),
    'DESCRIPTION:'+desc.replace(/[,\n]/g,x=>x===','?'\\,':'\\n'),
    'LOCATION:'+(addr||CFG.address).replace(/,/g,'\\,'),
    'END:VEVENT','END:VCALENDAR'].join('\r\n');
}
function downloadICS(content, filename) {
  const blob = new Blob([content], {type:'text/calendar;charset=utf-8'});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/* ── Step navigation ─────────────────────────────────────────────────── */
function goStep(n) {
  state.step = n;
  STEP_IDS.forEach((id, i) => { const el = $(id); if (el) el.hidden = (i + 1 !== n); });
  document.querySelectorAll('.kd-bk-prog__step').forEach(s => {
    const sn = parseInt(s.dataset.step);
    s.classList.toggle('kd-bk-prog__step--active', sn === n);
    s.classList.toggle('kd-bk-prog__step--done', sn < n);
  });

  const back = $('kd-bk-back'), nxt = $('kd-bk-next'), conf = $('kd-bk-confirm'), foot = $('kd-bk-footer');
  if (back) back.hidden = n <= 1 || n >= 6;
  if (conf) conf.hidden = n !== 5;
  if (foot) foot.hidden = n === 6;

  if (nxt) {
    // Show Next on step 1 (service), 3 (time), 4 (details); hide on date/review/success
    nxt.hidden = !(n === 1 || n === 3 || n === 4);
    if (n === 1) {
      const si = $('kd-bk-service-input');
      nxt.disabled = !si || !si.value.trim();
    } else if (n === 3) {
      nxt.disabled = !state.selectedSlot;
    } else {
      nxt.disabled = false;
    }
  }

  // Restore service text when returning to step 1
  if (n === 1) {
    const si = $('kd-bk-service-input');
    if (si && state.booking.service) si.value = state.booking.service;
  }

  if (n === 3) renderSlots();
  if (n === 5) renderReview();
}

/* ── Open / Close ────────────────────────────────────────────────────── */
function openModal(prefillService) {
  state.booking = {};
  state.selectedDate = null; state.selectedSlot = null;
  const si = $('kd-bk-service-input'); if (si) si.value = prefillService || '';
  const su = $('kd-bk-summary');       if (su) su.hidden = true;
  const now = new Date(); renderCal(now.getFullYear(), now.getMonth());
  goStep(1);
  /* Enable Next immediately if service was pre-filled */
  if (prefillService) {
    const nxtBtn = $('kd-bk-next');
    if (nxtBtn) nxtBtn.disabled = false;
  }
  const ov = $('kd-bk-overlay'); if (ov) { ov.hidden = false; ov.focus(); }
  document.body.style.overflow = 'hidden';
}
function closeModal() {
  const ov = $('kd-bk-overlay'); if (ov) ov.hidden = true;
  document.body.style.overflow = '';
}

/* ── Init ────────────────────────────────────────────────────────────── */
function init() {
  /* Calendar navigation */
  const prev = $('kd-cal-prev'), nxt = $('kd-cal-next');
  if (prev) prev.addEventListener('click', () => { let m = state.calMonth - 1, y = state.calYear; if (m < 0) { m = 11; y--; } renderCal(y, m); });
  if (nxt)  nxt.addEventListener('click',  () => { let m = state.calMonth + 1, y = state.calYear; if (m > 11) { m = 0; y++; } renderCal(y, m); });

  /* Service input — enable/disable Next as user types */
  const serviceInput = $('kd-bk-service-input');
  if (serviceInput) {
    serviceInput.addEventListener('input', () => {
      const nxtBtn = $('kd-bk-next');
      if (nxtBtn && state.step === 1) nxtBtn.disabled = !serviceInput.value.trim();
    });
  }

  /* Time input — validate on debounce and on blur */
  const timeInput = $('kd-bk-time-input');
  if (timeInput) {
    timeInput.addEventListener('input', () => {
      clearTimeout(_validationTimer);
      _validationTimer = setTimeout(validateTimeInput, 600);
    });
    timeInput.addEventListener('blur', () => {
      clearTimeout(_validationTimer);
      validateTimeInput();
    });
  }

  /* Footer buttons */
  const backBtn  = $('kd-bk-back'),
        nextBtn  = $('kd-bk-next'),
        confBtn  = $('kd-bk-confirm'),
        closeBtn = $('kd-bk-close'),
        doneBtn  = $('kd-bk-done');

  if (backBtn)  backBtn.addEventListener('click', () => goStep(state.step - 1));

  if (nextBtn) nextBtn.addEventListener('click', () => {
    if (state.step === 1) {
      const si  = $('kd-bk-service-input');
      const svc = si ? si.value.trim() : '';
      if (!svc) return;
      state.booking.service = svc;
      /* Show service in modal header summary */
      const ss = $('kd-bk-sum-service'), su = $('kd-bk-summary');
      if (ss) ss.textContent = svc;
      if (su) su.hidden = false;
      goStep(2);
    } else if (state.step === 3) {
      if (state.selectedSlot) goStep(4);
    } else if (state.step === 4) {
      if (validateContact()) goStep(5);
    }
  });

  if (confBtn)  confBtn.addEventListener('click', submitBooking);
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (doneBtn)  doneBtn.addEventListener('click', closeModal);

  const ov = $('kd-bk-overlay');
  if (ov) ov.addEventListener('click', e => { if (e.target === ov) closeModal(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && ov && !ov.hidden) closeModal(); });
}

if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
window.kerandyBooking = {open: openModal, close: closeModal};
})();
